import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import { endOfDay, isAfter } from 'date-fns';
import { isNil, prop, sortBy } from 'ramda';
import { useDispatch } from 'react-redux';

import ToggleButton from 'components/atoms/GefMui/ToggleButton';
import codelistProvider from 'components/decorators/codelistProvider';
import { codelists } from 'constants/codelists';
import { changeLeadFeedback } from 'features/views/marketPlace/actions';
import { feedbackType } from 'features/views/marketPlace/constants';
import { filterCodelistById } from 'utils';

import LeadFeedbackConfirm from './LeadFeedbackConfirm';

export const LeadFeedback = ({ lead, finishTypesCodelist, type }) => {
	const dispatch = useDispatch();
	const filteredFeedbacks = filterCodelistById(lead.taskTypeId, finishTypesCodelist, 'taskTypeId');
	const sortedFeedbacks = sortBy(prop('finishTypeId'))(filteredFeedbacks);

	const [selectedFeedback, changeSelectedFeedback] = useState(null);

	const handleClick = useCallback(
		(leadId, newFeedback) => () => {
			if (newFeedback.progressType) {
				changeSelectedFeedback(newFeedback);
				dispatch(changeLeadFeedback(leadId, newFeedback.finishTypeId, type));
			} else {
				changeSelectedFeedback(newFeedback);
			}
		},
		[dispatch, type],
	);

	// using either temporarily selected value or value from the lead (previously set feedback)
	const finalFeedbackValue = selectedFeedback?.finishTypeId || lead?.finishTypeId;

	const today = new Date();
	const endDate = endOfDay(new Date(lead.deadlineDate));
	const isAfterDeadline = isAfter(today, new Date(endDate));
	const isAfterDeadlineAndDoesntHaveInProgressFeedback = isAfterDeadline && isNil(lead.finishTypeId);

	return (
		<Stack spacing={3}>
			<Stack direction="row" spacing={2}>
				{sortedFeedbacks.map((feedback) => {
					return (
						<ToggleButton
							key={feedback.id}
							label={feedback.description}
							onClick={handleClick(lead.id, feedback)}
							selected={Number(feedback.finishTypeId) === finalFeedbackValue}
							disabled={lead.statusId === 3 || isAfterDeadlineAndDoesntHaveInProgressFeedback}
						/>
					);
				})}
			</Stack>
			{selectedFeedback?.progressType === false && (
				<LeadFeedbackConfirm
					selectedFeedbackId={selectedFeedback.finishTypeId}
					leadId={lead.id}
					changeSelectedFeedback={changeSelectedFeedback}
					type={type}
				/>
			)}
		</Stack>
	);
};

LeadFeedback.propTypes = {
	finishTypesCodelist: PropTypes.array,
	lead: PropTypes.object,
	type: PropTypes.oneOf([feedbackType.IN_LIST, feedbackType.IN_DETAIL]),
};

export default codelistProvider(codelists.finishTypes, 'finishTypesCodelist')(LeadFeedback);
